import NextImage from 'next/image'

// The idea with this component is to get the best of both worlds when loading images
// We don't want to eagerly load all images at initial page load
// but we do want to eager load our images before the user scrolls them into view

// Additionally we can use this as a place to set a default loading image placeholder.

// fetchPriority="high" preload="false" adds loading="lazy" to the img tag.
// fetchPriority="high" preload="true" simply removes loading="lazy" from the img tag.

/**
 * Available image size breakpoints matching next.config.js imageSizes + deviceSizes.
 * Used to snap `sizes` values up to the next available width so the browser never
 * picks a srcset candidate smaller than what the image actually needs (especially
 * on 1x DPR screens like Windows 2K monitors).
 */
const IMAGE_BREAKPOINTS = [
  16, 32, 48, 64, 96, 128, 192, 256, 384, 480, 540, 640, 768, 1024, 1280, 1440,
]

/**
 * Rounds a width up to the next available image breakpoint.
 * E.g. 400 → 480, 500 → 540, 256 → 256
 */
function snapToNextBreakpoint(width) {
  for (const bp of IMAGE_BREAKPOINTS) {
    if (bp >= width) return bp
  }
  return IMAGE_BREAKPOINTS[IMAGE_BREAKPOINTS.length - 1]
}

/**
 * Generates an optimal `sizes` attribute based on the image's intrinsic width.
 * This prevents the browser from downloading oversized images (e.g. 1440px) for
 * small UI elements like icons, avatars, or logos.
 *
 * Values are snapped up to the next available breakpoint so the browser always
 * picks a srcset candidate >= the actual display size, even at 1x DPR.
 *
 * - If the image width is known and <= 256px, use a fixed pixel size (small image).
 * - If the image width is known and <= 540px, cap at the snapped width on larger screens.
 * - Otherwise, fall back to full-width responsive behavior.
 */
function getResponsiveSizes(width) {
  const numericWidth = typeof width === 'string' ? parseInt(width, 10) : width

  // Small images (icons, avatars, badges): snap to next breakpoint for crisp rendering
  if (typeof numericWidth === 'number' && !isNaN(numericWidth) && numericWidth <= 256) {
    return `${snapToNextBreakpoint(numericWidth)}px`
  }

  // Medium images: snap to next breakpoint so 1x DPR screens don't undershoot
  if (typeof numericWidth === 'number' && !isNaN(numericWidth) && numericWidth <= 540) {
    const snappedWidth = snapToNextBreakpoint(numericWidth)
    return `(min-width: 1024px) ${snappedWidth}px, (min-width: 640px) ${Math.min(
      snappedWidth,
      768
    )}px, 100vw`
  }

  // Large / full-width images: use responsive breakpoints
  return '(min-width: 1024px) 1440px, (min-width: 640px) 768px, 100vw'
}

function toDimension(value) {
  if (value == null || value === '') return undefined
  const n = typeof value === 'string' ? parseInt(value, 10) : Number(value)
  return Number.isFinite(n) && n > 0 ? n : undefined
}

// Centralized webp quality for every image on the site. Keep in sync with the
// default in `src/lib/contentfulLoader.js` (used when no `quality` is passed).
const DEFAULT_IMAGE_QUALITY = 65

function Image(props) {
  const { src, alt, altText, sizes, width, height, fill, quality, ...rest } = props

  const trimmedSrc = typeof src === 'string' ? src.trim() : ''
  if (!trimmedSrc) return null

  const useFill = Boolean(fill)
  let resolvedWidth = toDimension(width)
  let resolvedHeight = toDimension(height)

  if (!useFill && (resolvedWidth == null || resolvedHeight == null)) {
    // next/image requires width+height unless `fill`. CMS SVGs often omit dimensions in metadata.
    if (/\.svg(\?|#|$)/i.test(trimmedSrc)) {
      resolvedWidth = resolvedWidth ?? 64
      resolvedHeight = resolvedHeight ?? 64
    } else {
      resolvedWidth = resolvedWidth ?? 1200
      resolvedHeight = resolvedHeight ?? 800
    }
  }

  const imgProps = {
    // Use caller-provided sizes if explicitly set, otherwise compute from width
    sizes: sizes || getResponsiveSizes(resolvedWidth ?? width),
    // Normalize webp quality sitewide; callers can still override explicitly.
    quality: quality ?? DEFAULT_IMAGE_QUALITY,
  }

  // `altText` is common from CMS fields but is not a valid DOM/img prop — map to `alt` only.
  // `src` / `width` / `height` / `fill` must be explicit — removed from `...rest` by destructuring.
  return (
    <NextImage
      alt={alt ?? altText ?? ''}
      {...imgProps}
      {...rest}
      {...(useFill ? { fill: true } : { width: resolvedWidth, height: resolvedHeight })}
      src={trimmedSrc}
    />
  )
}

export { Image }
